import { motion } from 'framer-motion';
import { useReveal } from '../../hooks/useReveal';

const PRINCIPLES = [
    { n: '01', title: 'Clear updates, not confusion.', body: 'You always know where the project stands, what we did this week, and what is next.' },
    { n: '02', title: 'Honest advice, not pressure.', body: 'We diagnose first. If the right call is to delay, scope smaller, or not buy at all — we will say so.' },
    { n: '03', title: 'Respect for your time.', body: 'We show up prepared, respond properly, and keep the work moving. Your calendar is not a parking lot for our questions.' },
    { n: '04', title: 'Ownership, not excuses.', body: 'When we take a project, we take responsibility — for the standard, the communication, and the follow-through.' },
    { n: '05', title: 'Human communication, no ego.', body: 'Direct, considered, plain-spoken. Just adults solving a real problem together.' },
];

const ease = [0.22, 1, 0.36, 1];

export function WhyChooseUs() {
    const ref = useReveal();
    return (
        <section
            id="why-us"
            data-testid="why-choose-us-section"
            className="relative section overflow-hidden"
        >
            <div className="atmosphere opacity-50" />
            <div className="shell relative">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-20 items-start">
                    <div className="lg:sticky lg:top-28">
                        <p className="eyebrow">Conduct · How we work with you</p>
                        <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] leading-[1.02] tracking-[-0.04em] text-white">
                            What you{' '}
                            <span className="text-gradient-cyan">actually</span>
                            {' '}get when you hire us.
                        </h2>
                        <p className="mt-7 text-[17px] leading-[1.65] text-slate-300 max-w-md">
                            Most clients did not come to us looking for a
                            website. They came looking for a studio that would
                            treat their business with the same seriousness they
                            do.
                        </p>
                    </div>

                    <ol className="border-t border-white/[0.08]">
                        {PRINCIPLES.map((p, i) => (
                            <Principle key={p.n} {...p} idx={i} />
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
}

function Principle({ n, title, body, idx }) {
    return (
        <motion.li
            data-testid={`principle-${idx}`}
            className="group list-none py-7 lg:py-8 border-b border-white/[0.08] grid grid-cols-[44px_1fr] gap-6 transition-colors duration-300 hover:bg-white/[0.015]"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: idx * 0.07, ease }}
        >
            <span className="font-mono text-[12px] tracking-[0.22em] font-medium text-cyan-400 pt-1.5">
                {n}
            </span>
            <div>
                <h3 className="font-display font-semibold text-[1.4rem] sm:text-[1.6rem] tracking-[-0.025em] text-slate-300 group-hover:text-white transition-colors duration-300 leading-[1.2]">
                    {title}
                </h3>
                <p className="mt-3 text-[15px] leading-[1.65] text-slate-500 group-hover:text-slate-300 transition-colors duration-300 max-w-xl">
                    {body}
                </p>
            </div>
        </motion.li>
    );
}
